import React, { useMemo } from "react";
import { Tooltip } from "antd";
import Table, { Column } from "../../../../atoms/Table/Table";
import { PRIMARY_STYLES, STATUS_META } from "../../constants";
import { getSessionActions } from "../../status/getSessionActions";
import { WebinarSession, WebinarTopic } from "../../types/models";
import { formatSessionDateParts } from "../../utils/deriveStartAt";
import "./WebinarTable.scss";

type Props = {
  sessions: WebinarSession[];
  topics: WebinarTopic[];
  /** True when the unfiltered list is empty (vs filter/search miss). */
  isCatalogEmpty?: boolean;
  /** Chief coaches only — create / edit / delete / duplicate. */
  canManage?: boolean;
  /** Hide Share column (e.g. cancelled filter). */
  showShareColumn?: boolean;
  onPrimary: (session: WebinarSession) => void;
  onEdit: (session: WebinarSession) => void;
  onDuplicate: (session: WebinarSession) => void;
  onCancel: (session: WebinarSession) => void;
  onDeleteDraft: (session: WebinarSession) => void;
  onShare: (session: WebinarSession) => void;
  onCreate?: () => void;
};

function statusNote(s: WebinarSession): string {
  if (s.status === "scheduled") return `${s.reminders} reminded`;
  if (s.status === "live") {
    const mins = Math.max(1, Math.round((Date.now() - s.start) / 60000));
    return `${mins} min in`;
  }
  if (s.status === "ended") return `${s.reminders} joined`;
  if (s.status === "draft") return "Not visible";
  return "Members notified";
}

function statusMetaFor(s: WebinarSession) {
  if (
    s.rescheduled &&
    (s.status === "scheduled" || s.backendStatus === "scheduled")
  ) {
    return {
      label: "RESCHEDULED",
      bg: "#F2EBE1",
      fg: "#9A7A3A",
      dot: "#9A7A3A",
    };
  }
  return STATUS_META[s.status];
}

const IconEdit = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M16.5 3.5l4 4L8 20H4v-4z" />
  </svg>
);

const IconDuplicate = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="8.5" y="8.5" width="12" height="12" rx="3" />
    <path d="M15.5 5.5H6a2.5 2.5 0 00-2.5 2.5v9.5" />
  </svg>
);

const IconCancel = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.1"
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const IconShare = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="18" cy="5" r="2.5" />
    <circle cx="6" cy="12" r="2.5" />
    <circle cx="18" cy="19" r="2.5" />
    <path d="M8.4 13.2l7.2 4.1M15.6 6.7l-7.2 4.1" />
  </svg>
);

const IconSeries = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M4 12a8 8 0 0113-6M20 12a8 8 0 01-13 6" />
    <path d="M17 3v3.5h-3.5M7 21v-3.5h3.5" />
  </svg>
);

const IconLock = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    aria-hidden
  >
    <rect x="4" y="10.5" width="16" height="11" rx="3" />
    <path d="M8 10.5V7.5a4 4 0 018 0v3" />
  </svg>
);

const IconHostFallback = () => (
  <svg width="30" height="30" viewBox="0 0 64 64" aria-hidden>
    <circle cx="32" cy="24" r="11" fill="#A09890" />
    <path d="M32 38c-11 0-19 7-21 17h42c-2-10-10-17-21-17z" fill="#A09890" />
  </svg>
);

const WebinarTable: React.FC<Props> = ({
  sessions,
  topics,
  isCatalogEmpty = false,
  canManage = true,
  showShareColumn = true,
  onPrimary,
  onEdit,
  onDuplicate,
  onCancel,
  onDeleteDraft,
  onShare,
  onCreate,
}) => {
  const topicMap = useMemo(
    () => Object.fromEntries(topics.map((t) => [t.id, t])),
    [topics],
  );

  const columns: Column[] = useMemo(
    () => [
      {
        label: "Session",
        accessor: "title",
        width: 320,
        render: (_value, row: WebinarSession) => {
          const topic = topicMap[row.topicId];
          return (
            <div className="wf-table__session">
              <div className="wf-table__tags">
                {topic || row.topicName ? (
                  <span
                    className="wf-chip wf-chip--topic"
                    style={{
                      background: topic?.bg || "#F5F1EC",
                      color: topic?.fg || "#5C554E",
                    }}
                  >
                    {topic?.name || row.topicName}
                  </span>
                ) : null}
                {row.recurrence ? (
                  <span className="wf-chip wf-chip--series">
                    <IconSeries />
                    {row.recurrence}
                  </span>
                ) : null}
                {row.rescheduled ? (
                  <span className="wf-chip wf-chip--warn">RESCHEDULED</span>
                ) : null}
              </div>
              <div className="wf-table__title">{row.title}</div>
              <div className="wf-table__oneliner">{row.oneLiner}</div>
            </div>
          );
        },
      },
      {
        label: "Host",
        accessor: "hostName",
        width: 168,
        render: (_value, row: WebinarSession) => (
          <div className="wf-table__host">
            <div className="wf-avatar" aria-hidden>
              {row.hostPhoto ? (
                <img src={row.hostPhoto} alt="" />
              ) : (
                <IconHostFallback />
              )}
            </div>
            <div className="wf-table__host-text">
              <div className="wf-table__host-name">
                {row.hostName || "Host not set"}
              </div>
              <div className="wf-table__host-role">{row.hostRole}</div>
            </div>
          </div>
        ),
      },
      {
        label: "When",
        accessor: "start",
        width: 168,
        render: (_value, row: WebinarSession) => {
          const dateParts = formatSessionDateParts(row.start, row.mins);
          return (
            <div className="wf-table__when">
              <strong>{dateParts?.dateLine}</strong>
              <br />
              {dateParts?.timeLine}
            </div>
          );
        },
      },
      {
        label: "Access",
        accessor: "access",
        width: 116,
        render: (_value, row: WebinarSession) => (
          <div className="wf-table__access">
            <span
              className={`wf-access__chip ${
                row.access === "members" ? "is-members" : "is-everyone"
              }`}
            >
              {row.access === "members" ? <IconLock /> : null}
              {row.access === "members" ? "Members" : "Everyone"}
            </span>
          </div>
        ),
      },
      {
        label: "Status",
        accessor: "status",
        width: 132,
        render: (_value, row: WebinarSession) => {
          const sm = statusMetaFor(row);
          return (
            <div className="wf-table__status">
              <span
                className={`wf-status${row.status === "live" ? " is-live" : ""}`}
                style={{ background: sm.bg, color: sm.fg }}
              >
                <span
                  className="wf-status__dot"
                  style={{ background: sm.dot }}
                />
                {sm.label}
              </span>
              <div className="wf-status__note">{statusNote(row)}</div>
            </div>
          );
        },
      },
      ...(showShareColumn
        ? [
            {
              label: "Share",
              accessor: "share",
              width: 72,
              render: (_value: unknown, row: WebinarSession) => {
                if (row.status === "cancelled" || row.status === "ended") {
                  return null;
                }
                return (
                  <div className="wf-table__share">
                    <Tooltip title="Share app session link" placement="top">
                      <button
                        type="button"
                        className="wf-icon-btn"
                        aria-label="Share app session link"
                        onClick={(e) => {
                          e.stopPropagation();
                          onShare(row);
                        }}
                      >
                        <IconShare />
                        Share
                      </button>
                    </Tooltip>
                  </div>
                );
              },
            } satisfies Column,
          ]
        : []),
      {
        label: "Actions (Do not Share)",
        accessor: "id",
        width: 250,
        render: (_value, row: WebinarSession) => {
          const actions = getSessionActions(row, { canManage });
          const prim =
            actions.primary === "copy_join_link"
              ? PRIMARY_STYLES.scheduled
              : PRIMARY_STYLES[row.status];
          const showRemove = actions.canCancel || actions.canDeleteDraft;
          return (
            <div className="wf-table__actions">
              {actions.primary ? (
                <Tooltip
                  title="Do not share this joining link with users."
                  placement="top"
                >
                  <button
                    type="button"
                    className="wf-btn-primary"
                    style={{
                      background: prim.bg,
                      color: prim.fg,
                      borderColor: prim.border,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrimary(row);
                    }}
                  >
                    {actions.primaryLabel}
                  </button>
                </Tooltip>
              ) : null}

              {actions.canEdit ? (
                <Tooltip title="Edit session" placement="top">
                  <button
                    type="button"
                    className="wf-icon-btn"
                    aria-label="Edit session"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(row);
                    }}
                  >
                    <IconEdit />
                  </button>
                </Tooltip>
              ) : null}

              {actions.canDuplicate ? (
                <Tooltip title="Duplicate session" placement="top">
                  <button
                    type="button"
                    className="wf-icon-btn"
                    aria-label="Duplicate session"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate(row);
                    }}
                  >
                    <IconDuplicate />
                  </button>
                </Tooltip>
              ) : null}

              {showRemove ? (
                <Tooltip
                  title={
                    actions.canDeleteDraft ? "Delete draft" : "Cancel session"
                  }
                  placement="top"
                >
                  <button
                    type="button"
                    className="wf-icon-btn wf-icon-btn--danger"
                    aria-label={
                      actions.canDeleteDraft ? "Delete draft" : "Cancel session"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (actions.canDeleteDraft) onDeleteDraft(row);
                      else onCancel(row);
                    }}
                  >
                    <IconCancel />
                  </button>
                </Tooltip>
              ) : null}
            </div>
          );
        },
      },
    ],
    [
      topicMap,
      canManage,
      showShareColumn,
      onPrimary,
      onEdit,
      onDuplicate,
      onCancel,
      onDeleteDraft,
      onShare,
    ],
  );

  if (sessions.length === 0) {
    return (
      <div className="wf-table-empty">
        <div className="wf-table-empty__title">
          {isCatalogEmpty ? "No live sessions yet" : "No sessions in this view"}
        </div>
        <div className="wf-table-empty__sub">
          {isCatalogEmpty
            ? canManage
              ? "Create your first session to plan, publish and run it for members."
              : "No live sessions yet. A chief coach can create one."
            : canManage
              ? "Change the filter or search, or start a new session."
              : "Change the filter or search to find a session."}
        </div>
        {isCatalogEmpty && onCreate && canManage ? (
          <button
            type="button"
            className="wf-btn dark wf-table-empty__cta"
            onClick={onCreate}
          >
            New session
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="wf-table">
      <Table data={sessions} columns={columns} rowKey="id" />
    </div>
  );
};

export default WebinarTable;
